import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { getSingleProduct, getProductsByCategory, getProductReviews } from "../services/productService";
import { FaCheckCircle, FaStar, FaArrowRight } from "react-icons/fa";
import { Plus } from "lucide-react";
import { useCart } from "../contexts/CartContext";
import "../styles/ProductPage.css";
import { BASE_URL, IMAGE_BASE_URL } from "../api/api";

function ProductPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { addToCart, getCartItemsCount } = useCart();

  const [product, setProduct] = useState(null);
  const [selectedImage, setSelectedImage] = useState(null);
  const [selectedVariant, setSelectedVariant] = useState(null);
  const [quantity, setQuantity] = useState(1);
  const [showModal, setShowModal] = useState(false);
  const [activeTab, setActiveTab] = useState("description");
  const [reviews, setReviews] = useState([]);
  const [relatedProducts, setRelatedProducts] = useState([]);
  const [addingToCart, setAddingToCart] = useState(false);

  useEffect(() => {
    const fetchProductData = async () => {
      try {
        const productData = await getSingleProduct(id);
        setProduct(productData);

        // Default image
        const mainImg = productData.images.find((img) => img.is_primary);
        setSelectedImage(`${IMAGE_BASE_URL}storage/${mainImg.image_path}`);

        // Default variant
        if (productData.variants.length > 0) {
          setSelectedVariant(productData.variants[0]);
        }

        // Fetch reviews
        const reviewData = await getProductReviews(productData.id);
        setReviews(reviewData);

        // Fetch related products
        const related = await getProductsByCategory(productData.sub_category.category_id);
        setRelatedProducts(related.filter((p) => p.id !== productData.id));
      } catch (error) {
        console.error("Error loading product:", error);
      }
    };

    fetchProductData();
  }, [id]);

  const handleAddToCart = async () => {
    if (!product) return;

    setAddingToCart(true);

    try {
      const cartData = {
        product,
        selectedImage,
        selectedVariant,
        quantity
      };

      await addToCart(cartData);
      setShowModal(true);
    } catch (error) {
      console.error("Error adding to cart:", error);
      alert("Failed to add item to cart. Please try again.");
    } finally {
      setAddingToCart(false);
    }
  };

  if (!product) return <p className="text-center mt-5">Loading product...</p>;

  return (
    <div>
      {/* Breadcrumb */}
      <div className="container py-4">
        <p className="mb-5 routepath_title">
          <Link
            to="/"
            className="text-decoration-none routepath-link"
            style={{ color: '#6c757d' }}
          >
            Home
          </Link>
          <span className="mx-1">/</span>
          <Link
            to={`/category/${product.sub_category?.category?.id}`}
            className="text-decoration-none routepath-link"
            style={{ color: '#6c757d' }}
          >
            {product.sub_category?.category?.name}
          </Link>
          <span className="mx-1">/</span>
          {product.name}
        </p>

        <div className="row">
          {/* Product Images */}
          <div className="col-md-6 d-flex">
            <div className="d-flex flex-column me-3">
              {product.images.map((img, index) => (
                <img
                  key={index}
                  src={`${IMAGE_BASE_URL}storage/${img.image_path}`}
                  alt={product.name}
                  className={`img-thumbnail mb-2 ${selectedImage === `${IMAGE_BASE_URL}storage/${img.image_path}`
                    ? "border border-dark"
                    : ""
                    }`}
                  style={{ width: "80px", height: "80px", cursor: "pointer" }}
                  onClick={() => setSelectedImage(`${IMAGE_BASE_URL}storage/${img.image_path}`)}
                />
              ))}
            </div>

            <img
              src={selectedImage}
              alt={product.name}
              className="img-fluid border rounded"
              style={{ maxHeight: "400px", width: "100%", objectFit: "cover" }}
            />
          </div>

          {/* Product Details */}
          <div className="col-md-6">
            <p className="mb-3 product-title">{product.name}</p>
            <div className="mb-3">
              <span className="text-warning">⭐ 4.5</span>
              <span className="ms-2 text-muted">(120 reviews)</span>
            </div>

            <div className="mb-4 product-price">
              {product.sale_price ? (
                <>
                  <span className="text-muted text-decoration-line-through me-2">
                    Rs.{product.price}
                  </span>
                  <span className="text-danger fw-semibold">
                    Rs.{product.sale_price}
                  </span>
                </>
              ) : (
                <span>Rs.{product.price}</span>
              )}
            </div>

            {/* Variants */}
            {product.variants.length > 0 && (
              <div className="mb-4">
                <p className="mb-2">Select Variant:</p>
                <div className="d-flex gap-2 flex-wrap">
                  {product.variants.map((variant) => (
                    <button
                      key={variant.id}
                      className={`btn ${selectedVariant?.id === variant.id ? "text-white" : "btn-outline-dark"
                        }`}
                      style={
                        selectedVariant?.id === variant.id
                          ? { backgroundColor: "#FD7E14", borderColor: "#FD7E14" }
                          : {}
                      }
                      onClick={() => setSelectedVariant(variant)}
                    >
                      {variant.variant_name}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Quantity */}
            <div className="mb-4">
              <p className="mb-2">Quantity:</p>
              <div className="d-flex align-items-center">
                <button
                  className="btn btn-outline-dark"
                  onClick={() => setQuantity((q) => Math.max(1, q - 1))}
                  disabled={quantity <= 1}
                >
                  -
                </button>
                <span className="mx-3">{quantity}</span>
                <button
                  className="btn btn-outline-dark"
                  onClick={() => setQuantity((q) => q + 1)}
                >
                  +
                </button>
              </div>
            </div>

            {/* Action Buttons - Fixed Alignment */}
            <div className="row g-2 mb-4">
              <div className="col-sm-6">
                <button
                  className="btn w-100 py-2"
                  style={{ backgroundColor: "#FD7E14", color: "#fff", border: "none" }}
                  onClick={handleAddToCart}
                  disabled={addingToCart}
                >
                  {addingToCart ? "Adding..." : "Add to Cart"}
                </button>
              </div>
              <div className="col-sm-6">
                <button
                  className="btn w-100 py-2"
                  style={{ backgroundColor: "#28a745", color: "#fff", border: "none" }}
                  onClick={() => {
                    handleAddToCart();
                    navigate('/cart');
                  }}
                  disabled={addingToCart}
                >
                  Buy Now
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Tabs Section */}
        <div className="container py-3">
          <ul className="nav-tabs-simple">
            <li>
              <button
                className={activeTab === "description" ? "tab-active" : ""}
                onClick={() => setActiveTab("description")}
              >
                Description
              </button>
            </li>
            <li>
              <button
                className={activeTab === "reviews" ? "tab-active" : ""}
                onClick={() => setActiveTab("reviews")}
              >
                Reviews <span className="badge bg-secondary">{reviews.length}</span>
              </button>
            </li>
            <li>
              <button
                className={activeTab === "support" ? "tab-active" : ""}
                onClick={() => setActiveTab("support")}
              >
                Support
              </button>
            </li>
          </ul>

          <div className="tab-content-simple mt-3">
            {activeTab === "description" && (
              <p>{product.description || "No description available."}</p>
            )}

            {activeTab === "reviews" && (
              <div>
                {reviews.map((rev) => (
                  <div key={rev.id} className="mb-2 border-bottom pb-2">
                    <strong>{rev.name}</strong>{" "}
                    {[...Array(5)].map((_, i) => (
                      <FaStar key={i} color={i < rev.rating ? "#FD7E14" : "#ccc"} />
                    ))}
                    <p>{rev.comment}</p>
                  </div>
                ))}
              </div>
            )}

            {activeTab === "support" && (
              <p>For help, contact our support team at <strong>litegrocer@gmail.com</strong></p>
            )}
          </div>
        </div>

        {/* You May Also Like Section */}
        <div className="container my-5">
          <h5 className="mb-3">You May Also Like</h5>
          <div className="d-flex flex-wrap gap-3">
            {relatedProducts.map((prod) => {
              const primaryImage = prod.images.find((img) => img.is_primary);
              return (
                <div
                  key={prod.id}
                  className="card border-0 product-card"
                  style={{ width: "180px", cursor: "pointer" }}
                  onClick={() => navigate(`/product/${prod.id}`)}
                >
                  <div className="position-relative">
                    <img
                      src={
                        primaryImage
                          ? `${IMAGE_BASE_URL}storage/${primaryImage.image_path}`
                          : "https://media.istockphoto.com/id/1147544807/vector/thumbnail-image-vector-graphic.jpg?s=612x612&w=0&k=20&c=rnCKVbdxqkjlcs3xH87-9gocETqpspHFXu5dIGB4wuM="
                      }
                      className="card-img-top"
                      alt={prod.name}
                    />
                    <button
                      className="position-absolute d-flex justify-content-center align-items-center"
                      style={{
                        bottom: "10px",
                        right: "10px",
                        width: "35px",
                        height: "35px",
                        borderRadius: "50%",
                        backgroundColor: "#FD7E14",
                        border: "none",
                      }}
                    >
                      <Plus size={18} color="white" />
                    </button>
                  </div>
                  <div className="card-body p-2">
                    <h6 className="card-title mb-1">{prod.name}</h6>
                    <div className="mb-0">
                      {prod.sale_price ? (
                        <>
                          <span className="text-muted text-decoration-line-through me-2">
                            Rs.{prod.price}
                          </span>
                          <span className="text-danger fw-semibold">
                            Rs.{prod.sale_price}
                          </span>
                        </>
                      ) : (
                        <span>Rs.{prod.price}</span>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Add to Cart Modal */}
        {showModal && (
          <div
            className="modal fade show"
            style={{ display: "block", backgroundColor: "rgba(0,0,0,0.5)" }}
            onClick={() => setShowModal(false)}
          >
            <div className="modal-dialog modal-dialog-centered" style={{ maxWidth: "400px" }}>
              <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                <div className="modal-header">
                  <p className="modal-title d-flex align-items-center gap-2">
                    <FaCheckCircle className="text-success" /> Added to Cart
                  </p>
                  <button
                    type="button"
                    className="btn-close"
                    onClick={() => setShowModal(false)}
                  ></button>
                </div>
                <div className="modal-body d-flex align-items-center">
                  <img
                    src={selectedImage}
                    alt={product.name}
                    className="img-thumbnail me-3"
                    style={{ width: "80px", height: "80px", objectFit: "cover" }}
                  />
                  <div>
                    <p className="mb-1 fw-bold">{product.name}</p>
                    {selectedVariant && <p className="mb-1 text-muted">{selectedVariant.variant_name}</p>}
                    <p className="mb-0">Qty: {quantity}</p>
                  </div>
                </div>
                <div className="modal-footer">
                  <button
                    className="btn btn-outline-secondary"
                    onClick={() => setShowModal(false)}
                  >
                    Continue Shopping
                  </button>
                  <button
                    className="btn text-white"
                    style={{ backgroundColor: "#FD7E14" }}
                    onClick={() => {
                      setShowModal(false);
                      navigate('/cart');
                    }}
                  >
                    View Cart ({getCartItemsCount()})
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default ProductPage;